/** Part constructors and site readers shared by the megaproject grammars. Pure. */
import { createRng, nextFloat, type Rng } from '../../core/rng.ts'
import type { MegaPart, MegaRegion, PartRole, PrimKind, SiteContext, Vec3 } from './types.ts'

export interface PartOpts {
  readonly rot?: Vec3
  readonly taper?: number
  readonly shear?: number
  readonly arc?: number
  readonly step?: number
  readonly role?: PartRole
  readonly seed?: number
  readonly tint?: string
}

export function part(prim: PrimKind, pos: Vec3, size: Vec3, o: PartOpts = {}): MegaPart {
  const base = {
    prim,
    pos,
    rot: o.rot ?? ([0, 0, 0] as const),
    size,
    taper: o.taper ?? 1,
    shear: o.shear ?? 0,
    arc: o.arc ?? 0,
    step: o.step ?? 1,
    role: o.role ?? 'body',
    seed: o.seed ?? 0,
  }
  return o.tint === undefined ? base : { ...base, tint: o.tint }
}

/** Unit direction in the disc plane for a bearing (0 = +x, π/2 = +z). */
export function dir(bearing: number): readonly [number, number] {
  return [Math.cos(bearing), Math.sin(bearing)]
}

/** Yaw that turns a part's local +x towards a bearing in the disc plane. */
export function yawTo(bearing: number): number {
  return -bearing
}

/** A part centred `r` out from the axis at a bearing and height y, its local x pointing outward. */
export function radial(prim: PrimKind, bearing: number, r: number, y: number, size: Vec3, o: PartOpts = {}): MegaPart {
  const [c, s] = dir(bearing)
  const rot = o.rot ?? [0, 0, 0]
  return part(prim, [c * r, y, s * r], size, { ...o, rot: [rot[0], yawTo(bearing) + rot[1], rot[2]] })
}

/** Star luminosity class 0..3: 0 = dim (biggest sails and collectors), 3 = bright. */
export function lumClass(site: SiteContext): number {
  const l = site.starLuminosity
  if (l < 0.4) return 0
  if (l < 1.2) return 1
  if (l < 3) return 2
  return 3
}

/** Collector or sail scale from the star: a dim star doubles the sail against a bright one. */
export function lumScale(site: SiteContext): number {
  return [1.7, 1.35, 1.1, 0.85][lumClass(site)]
}

/** Gate bearing quantised to 4 quadrants (0 = +x), for the slot code. */
export function gateQuadrant(site: SiteContext): number {
  const q = Math.round(site.gateBearingRad / (Math.PI / 2))
  return ((q % 4) + 4) % 4
}

const REGION_INDEX: Record<MegaRegion, number> = { gate: 0, mid: 1, deep: 2, rim: 3 }
export function regionIndex(site: SiteContext): number {
  return REGION_INDEX[site.region]
}

/** Ore class 0..3 from the ore tint (hue quadrant); no tint reads as class 0. */
export function oreClass(site: SiteContext): number {
  const t = site.oreTint
  if (t === undefined || !/^#[0-9a-fA-F]{6}$/.test(t)) return 0
  const r = parseInt(t.slice(1, 3), 16) / 255
  const g = parseInt(t.slice(3, 5), 16) / 255
  const b = parseInt(t.slice(5, 7), 16) / 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  if (max - min < 0.08) return 0
  let h: number
  if (max === r) h = ((g - b) / (max - min) + 6) % 6
  else if (max === g) h = (b - r) / (max - min) + 2
  else h = (r - g) / (max - min) + 4
  return Math.floor(h / 1.5) % 4
}

/** Integer hash of a seed with labelled integers (murmur3 fmix32 rounds); deterministic on every platform. */
export function hashSeed(seed: number, ...parts: number[]): number {
  let h = (seed ^ 0x9e3779b9) >>> 0
  for (const p of parts) {
    h = Math.imul(h ^ (p >>> 0), 0x85ebca6b) >>> 0
    h ^= h >>> 13
    h = Math.imul(h, 0xc2b2ae35) >>> 0
    h ^= h >>> 16
  }
  h ^= h >>> 16
  h = Math.imul(h, 0x85ebca6b) >>> 0
  h ^= h >>> 13
  return h >>> 0
}

/** Linear interpolation. */
export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t
}

/** A detail generator for scatter inside one building (pods, teeth, lumps): seeded from the genome's detail seed. */
export function detailRng(detail: number, salt: number): Rng {
  return createRng(hashSeed(detail, salt))
}

/** Uniform float in [a, b). */
export function rf(rng: Rng, a: number, b: number): number {
  return a + (b - a) * nextFloat(rng)
}

/** Uniform integer in [0, n). */
export function ri(rng: Rng, n: number): number {
  return Math.floor(nextFloat(rng) * n)
}

/** Euler rotation (three.js XYZ) that turns a part's local +y onto unit vector `d`. */
export function rotYTo(d: Vec3): Vec3 {
  return [Math.atan2(d[2], d[1]), 0, Math.atan2(-d[0], Math.hypot(d[1], d[2]))]
}

/** A part spanning P → Q along its local y (thickness `w` × `w2`). */
export function segment(prim: PrimKind, a: Vec3, b: Vec3, w: number, w2: number, o: PartOpts = {}): MegaPart {
  const d: Vec3 = [b[0] - a[0], b[1] - a[1], b[2] - a[2]]
  const len = Math.hypot(d[0], d[1], d[2]) || 1
  const u: Vec3 = [d[0] / len, d[1] / len, d[2] / len]
  return part(prim, [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2], [w, len, w2], { ...o, rot: rotYTo(u) })
}
